import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Device, deviceService } from "@/services/deviceService";
import { Calendar, MapPin, Building2, Smartphone, Wifi, WifiOff, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface DeviceDetailsDialogProps {
  device: Device | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function DeviceDetailsDialog({
  device,
  open,
  onOpenChange,
}: DeviceDetailsDialogProps) {
  const [cacheInterval, setCacheInterval] = useState<'off' | '6h' | '12h' | '24h'>(
    device?.cacheCleanupInterval || 'off'
  );
  const [isUpdatingCache, setIsUpdatingCache] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);

  if (!device) return null;

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleCacheIntervalChange = async (value: 'off' | '6h' | '12h' | '24h') => {
    setIsUpdatingCache(true);
    try {
      await deviceService.updateCacheSettings(device._id, value);
      setCacheInterval(value);
      toast.success('Cache ayarları güncellendi');
    } catch (error) {
      toast.error('Cache ayarları güncellenirken hata oluştu');
      console.error(error);
    } finally {
      setIsUpdatingCache(false);
    }
  };

  const handleManualCacheClear = async () => {
    setIsClearingCache(true);
    try {
      const result = await deviceService.clearCache(device._id);
      if (result.status === 'success') {
        toast.success('Cache temizleme komutu gönderildi');
      } else {
        toast.error(result.message || 'Cache temizleme başarısız');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Cache temizleme başarısız';
      toast.error(errorMessage);
      console.error(error);
    } finally {
      setIsClearingCache(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            {device.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Durum */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {device.isOnline ? (
                <>
                  <Wifi className="w-5 h-5 text-green-600" />
                  <Badge variant="success">Çevrimiçi</Badge>
                </>
              ) : (
                <>
                  <WifiOff className="w-5 h-5 text-gray-500" />
                  <Badge variant="secondary">Çevrimdışı</Badge>
                </>
              )}
            </div>
          </div>

          {/* Temel Bilgiler */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Token
              </h3>
              <p className="text-lg font-mono">{device.token}</p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Firma
              </h3>
              <p className="text-lg">{device.company?.name || "-"}</p>
            </div>
          </div>

          {/* Lokasyon Bilgileri */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Lokasyon
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Ülke</p>
                <p className="font-medium">
                  {device.location?.country?.name || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Şehir</p>
                <p className="font-medium">
                  {device.location?.city?.name || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">İlçe</p>
                <p className="font-medium">
                  {device.location?.district?.name || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Adres</p>
                <p className="font-medium">{device.location?.address || "-"}</p>
              </div>
            </div>
          </div>

          {/* Playlist Bilgisi */}
          {device.activePlaylist && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Aktif Playlist
              </h3>
              <p className="text-lg">{device.activePlaylist.name}</p>
            </div>
          )}

          {/* Grup Bilgisi */}
          {device.groupData && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Grup
              </h3>
              <p className="text-lg">{device.groupData.name}</p>
            </div>
          )}

          {/* Cihaz Bilgileri */}
          {device.deviceInfo && (
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">
                Cihaz Detayları
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                {device.deviceInfo.manufacturer && (
                  <div>
                    <p className="text-muted-foreground">Üretici</p>
                    <p className="font-medium">{device.deviceInfo.manufacturer}</p>
                  </div>
                )}
                {device.deviceInfo.model && (
                  <div>
                    <p className="text-muted-foreground">Model</p>
                    <p className="font-medium">{device.deviceInfo.model}</p>
                  </div>
                )}
                {device.deviceInfo.osVersion && (
                  <div>
                    <p className="text-muted-foreground">OS Versiyonu</p>
                    <p className="font-medium">{device.deviceInfo.osVersion}</p>
                  </div>
                )}
                {device.deviceInfo.appVersion && (
                  <div>
                    <p className="text-muted-foreground">Uygulama Versiyonu</p>
                    <p className="font-medium">{device.deviceInfo.appVersion}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cache Yönetimi */}
          <div className="pt-4 border-t">
            <h3 className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
              <Trash2 className="w-4 h-4" />
              Cache Yönetimi
            </h3>

            <div className="space-y-4">
              {/* Otomatik Cache Temizleme */}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Otomatik Temizleme Aralığı
                </label>
                <Select
                  value={cacheInterval}
                  onValueChange={handleCacheIntervalChange}
                  disabled={isUpdatingCache}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Kapalı</SelectItem>
                    <SelectItem value="6h">Her 6 Saatte</SelectItem>
                    <SelectItem value="12h">Her 12 Saatte</SelectItem>
                    <SelectItem value="24h">Her 24 Saatte</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-2">
                  Memory leak önlemi için otomatik cache temizleme. Şarkı dosyaları silinmez.
                </p>
              </div>

              {/* Son Otomatik Temizleme */}
              {device.lastAutoCacheCleanup && (
                <div>
                  <p className="text-sm text-muted-foreground">Son Otomatik Temizleme</p>
                  <p className="text-sm font-medium">{formatDate(device.lastAutoCacheCleanup)}</p>
                </div>
              )}

              {/* Manuel Temizleme */}
              <div className="pt-2">
                <Button
                  onClick={handleManualCacheClear}
                  disabled={isClearingCache || !device.isOnline}
                  variant="outline"
                  className="w-full"
                >
                  {isClearingCache ? 'Gönderiliyor...' : 'Cache Temizle (Manuel)'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  {device.isOnline
                    ? 'Cihaza anında cache temizleme komutu gönderir. Oynatma devam eder.'
                    : '⚠️ Cihaz çevrimdışı - Cache temizleme yapılamaz'}
                </p>
              </div>
            </div>
          </div>

          {/* Tarih Bilgileri */}
          <div className="pt-4 border-t">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Oluşturulma</p>
                  <p className="font-medium">{formatDate(device.createdAt)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Son Güncelleme</p>
                  <p className="font-medium">{formatDate(device.updatedAt)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
